import { Form, Input, Typography } from "antd";
import { useSelector } from "react-redux";
import "./styles.css";
import { OptionGroupSelect } from "@components/Compare/Components_1v1/OptionGroupSelect";
import {
    IAutomaticAction,
    PLAYER_NOTIFY_BODY_MAX_LENGTH,
    PLAYER_NOTIFY_FULL_TEAM,
    PLAYER_NOTIFY_TITLE_MAX_LENGTH
} from "@components/Settings/AutomaticActions/consts";

const { Text } = Typography;

// OptionGroupSelect searches the text inside a <span> label.
const toOption = (value: string, label: string) => ({ value, key: value, label: <span>{label}</span> });

// A new action starts on Full Team. An audience in an older shape loads empty, so the coach must pick again.
const toAudienceFormValue = (audience: unknown) => {
    if (audience === undefined) return [PLAYER_NOTIFY_FULL_TEAM];
    return Array.isArray(audience) ? audience : [];
};

// Full Team includes every other option, so it cannot share the selection with them.
const normalizeAudience = (next: string[] = [], prev: string[] = []) => {
    const addedFullTeam = next.includes(PLAYER_NOTIFY_FULL_TEAM) && !prev.includes(PLAYER_NOTIFY_FULL_TEAM);
    if (addedFullTeam) return [PLAYER_NOTIFY_FULL_TEAM];
    return next.length > 1 ? next.filter((token) => token !== PLAYER_NOTIFY_FULL_TEAM) : next;
};

export const toPlayerNotifyFormValues = (metadata: IAutomaticAction['metadata']) => ({
    notify_title: metadata?.title ?? '',
    notify_body: metadata?.body ?? '',
    notify_audience: toAudienceFormValue(metadata?.audience),
});

// The backend rejects any other metadata key for this action type, so send only these.
export const toPlayerNotifyMetadata = (values: any) => ({
    title: values.notify_title,
    body: values.notify_body,
    audience: values.notify_audience,
});

export const PlayerNotifyFields = ({ disabled }: { disabled: boolean }) => {
    const positionEntities = useSelector(({ team }: any) => team.positionEntities) as { id: string, name: string }[];
    const playerGroups = useSelector(({ team }: any) => team.playerGroups) as { player_group_id: number, name: string }[];
    const players = useSelector(({ team }: any) => team.players) as { player_id: number, first_name: string, last_name: string }[];

    const audienceOptions = [
        { label: <span>Team</span>, options: [toOption(PLAYER_NOTIFY_FULL_TEAM, 'Full Team')] },
        {
            label: <span>Smart Selectors</span>,
            options: [
                toOption('missing-wellness', "Players who have not yet submitted today's Wellness survey"),
                toOption('missing-rpe', "Players who have not yet submitted today's RPE survey"),
            ]
        },
        { label: <span>Players</span>, options: (players ?? []).map((p) => toOption(`player-${p.player_id}`, `${p.first_name} ${p.last_name}`)) },
        { label: <span>Positions</span>, options: (positionEntities ?? []).map((p) => toOption(`pos-${p.id}`, p.name)) },
        { label: <span>Groups</span>, options: (playerGroups ?? []).map((g) => toOption(`group-${g.player_group_id}`, g.name)) },
    ];

    return (
        <>
            <Form.Item
                label={<Text>Title</Text>}
                name="notify_title"
                rules={[{ required: true, whitespace: true, message: 'Please enter a title.' }]}
            >
                <Input
                    maxLength={PLAYER_NOTIFY_TITLE_MAX_LENGTH}
                    showCount
                    placeholder="Recovery check-in"
                    disabled={disabled}
                />
            </Form.Item>

            <Form.Item
                label={<Text>Message</Text>}
                name="notify_body"
                className="player-notify-message"
                rules={[{ required: true, whitespace: true, message: 'Please enter a message.' }]}
            >
                <Input.TextArea
                    maxLength={PLAYER_NOTIFY_BODY_MAX_LENGTH}
                    showCount
                    autoSize={{ minRows: 2, maxRows: 6 }}
                    placeholder="Log your wellness and RPE before you leave the facility."
                    disabled={disabled}
                />
            </Form.Item>

            <Form.Item
                label={<Text>Audience</Text>}
                name="notify_audience"
                extra="A player who matches any selection gets the notification."
                rules={[{ required: true, message: 'Please select an audience.' }]}
                valuePropName="selectedOptionVal"
                trigger="handleOptionGroupChange"
                normalize={normalizeAudience}
            >
                <OptionGroupSelect
                    placeholder="Select an audience"
                    options={audienceOptions}
                    width="100%"
                    sideId="player-notify"
                    disabled={disabled}
                />
            </Form.Item>
        </>
    );
};

export default PlayerNotifyFields;
